import React, { useEffect, useState } from 'react';
import { View, useWindowDimensions } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useT } from '@transifex/react';

import { AuthLayout } from '../components/AuthLayout';
import { useDsEnabled } from '../contexts/DesignSystem';
import { BloomButton } from '../components/BloomButton';
import { GlyphBadge } from '../components/Glyph';
import { InlineError } from '../components/InlineError';
import Loading from '../components/Loading';
import { Logo } from '../components/Logo';
import { PasswordInput } from '../components/PasswordInput';
import { PasswordInputWithStrength } from '../components/PasswordInputWithStrength';
import ThemedText from '../components/ThemedText';
import { ROUTES } from '../utils/constants';
import { screenTestId } from '../utils/testId';
import { validatePasswordStrength } from '../services/validationService';
import { validateResetToken, resetPassword } from '../api/auth';
import { analytics } from '../utils/analytics';
import { useLoginRouter } from '../hooks/useLoginRouter';

const tid = screenTestId('ResetPassword');

export default function ResetPasswordScreen() {
  const router = useLoginRouter();
  const params = useLocalSearchParams();
  const { width } = useWindowDimensions();
  const t = useT();
  const dsEnabled = useDsEnabled();

  const uidb64 = typeof params.uidb64 === 'string' ? params.uidb64 : '';
  const token = typeof params.token === 'string' ? params.token : '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [sessionToken, setSessionToken] = useState('');

  const isDesktop = width >= 768;

  const validation = validatePasswordStrength(password);
  const passwordsMatch = password === confirmPassword && password.length > 0 && confirmPassword.length > 0;
  const showConfirmError = confirmPassword.length > 0 && !passwordsMatch;
  const canSubmit = validation.isValid && passwordsMatch && !loading;

  useEffect(() => {
    if (!uidb64 || !token) {
      setValidating(false);
      setError(t('This reset link is invalid or has expired. Please request a new one.'));
      return;
    }

    validateResetToken({ uidb64, token })
      .then((data) => {
        analytics.trackEvent('reset_token_valid');
        setTokenValid(true);
        setSessionToken(data.session_token);
      })
      .catch(() => {
        analytics.trackEvent('reset_token_invalid');
        setError(t('This reset link is invalid or has expired. Please request a new one.'));
      })
      .finally(() => {
        setValidating(false);
      });
  }, [uidb64, token]);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setLoading(true);
    setError(null);
    analytics.trackEvent('reset_password_submitted');

    try {
      await resetPassword({
        uidb64,
        new_password1: password,
        new_password2: confirmPassword,
        session_token: sessionToken,
      });

      analytics.trackEvent('reset_password_succeeded');
      setSuccess(true);
      setTimeout(() => {
        router.push(ROUTES.LOGIN);
      }, 5000);
    } catch (err: any) {
      const message = err?.response?.data?.error || t('Failed to reset password. Please try again.');
      analytics.trackEvent('reset_password_failed', { error: message });
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <AuthLayout>
        <View className='w-full'>
          <Logo />
          <View className='items-center justify-center py-16'>
            <Loading className='flex-none' />
            <ThemedText type='bodyRegular' color='subtle' className='mt-4'>
              {t('Validating reset link...')}
            </ThemedText>
          </View>
        </View>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout>
        <View className='w-full'>
          <Logo />
          {dsEnabled ? (
            <View className='w-full'>
              <GlyphBadge name='check' testID={tid('success-badge')} />
              <ThemedText type='titleHero' className='mb-2'>
                {t('Password reset successfully!')}
              </ThemedText>
              <ThemedText type='bodyRegular' color='subtle'>
                {t('Redirecting to login...')}
              </ThemedText>
            </View>
          ) : (
            <View className='w-full items-center py-16'>
              <View className='bg-login-green-10 rounded-lg px-6 py-4 w-full mb-4'>
                <ThemedText type='bodyBold' color='success' className='text-center'>
                  {t('Password reset successfully!')}
                </ThemedText>
                <ThemedText type='bodySmall' color='subtle' className='text-center mt-1'>
                  {t('Redirecting to login...')}
                </ThemedText>
              </View>
            </View>
          )}
        </View>
      </AuthLayout>
    );
  }

  if (!tokenValid) {
    return (
      <AuthLayout>
        <View className='w-full'>
          <Logo />
          <View className='w-full'>
            {dsEnabled && <GlyphBadge name='clock-alert' testID={tid('expired-badge')} />}
            <ThemedText type='titleHero' className='mb-4 font-bold' style={{ fontSize: 32, lineHeight: 40 }}>
              {t('Reset password')}
            </ThemedText>
            <InlineError message={error} testID={tid('error')} />
            <View>
              <BloomButton
                text={t('Request New Reset Link')}
                onPress={() => router.push(ROUTES.FORGOT_PASSWORD)}
                testID={tid('request-new-link-button')}
              />
            </View>
          </View>
        </View>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <View className='w-full'>
        <Logo />

        <View className='w-full'>
          <ThemedText type='titleHero' className='mb-4 font-bold' style={{ fontSize: 32, lineHeight: 40 }}>
            {t('Reset password')}
          </ThemedText>

          <ThemedText type='bodyRegular' color='subtle' className='mb-8'>
            {t('A valid password must have a minimum of 8 characters and include 1 number, 1 capital letter, and 1 special character.')}
          </ThemedText>

          <InlineError message={error} testID={tid('form-error')} />

          <PasswordInputWithStrength
            label={t('New password')}
            value={password}
            onChangeText={setPassword}
            placeholder={t('New password')}
            showStrength={true}
            testID={tid('new-password-input')}
            onSubmitEditing={() => {}}
          />

          <PasswordInput
            label={t('Confirm New Password')}
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            placeholder={t('Confirm New Password')}
            error={showConfirmError ? t("Passwords don't match") : undefined}
            testID={tid('confirm-password-input')}
            onSubmitEditing={handleSubmit}
          />

          <View>
            <BloomButton
              text={t('Set Password')}
              onPress={handleSubmit}
              disabled={!canSubmit}
              loading={loading}
              testID={tid('set-password-button')}
            />
          </View>
        </View>
      </View>
    </AuthLayout>
  );
}
